import React, { useState, useEffect, useContext } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDatabase,
  faTrashCan,
  faChevronLeft,
  faChevronRight,
  faPenToSquare,
  faPlus,
  faTimesCircle,
  faCheckCircle,
  faSearch,
  faRefresh,
  faSlidersH,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ConfirmationPopup from "../deletePopup/deletePopup";
import { toast } from "react-toastify";
import { AppContext } from "../context/appContext";

const getNestedValue = (obj, path) =>
  path.split(".").reduce((acc, part) => acc && acc[part], obj);

const normalizeHeading = (heading) =>
  heading
    .replace(/[_\-.]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const ModelListing = () => {
  const location = useLocation();
  const { modelname } = useParams();
  const navigate = useNavigate();
  const { reloadContext, setReloadContext } = useContext(AppContext);

  const [modalData, setModalData] = useState([]);
  const [modalSchema, setModalSchema] = useState([]);
  const [filteredData, setFilteredModalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit] = useState(5);
  const [reload, setReload] = useState(false);
  const [filters, setFilters] = useState({ createdAt: "", updatedAt: "" });
  const [showFilter, setShowFilter] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteUserData, setDeleteUserData] = useState({});
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [busy, setBusy] = useState(false);

  const displayName = modelname
    ? modelname.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : "Model";

  useEffect(() => {
    if (location.state?.isEdited) {
      // Auto-reload the list and the global AppContext master data
      setReload((r) => !r);
      setReloadContext((c) => !c);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setBusy(true);
      try {
        const params = { page: currentPage, limit };
        let res;
        if (searchTerm.trim()) {
          res = await func.get(`mst-admin/${modelname}/search-mst-data`, { params: { ...params, query: searchTerm.trim() } });
        } else if (Object.values(filters).some(Boolean)) {
          res = await func.post(`mst-admin/${modelname}/filter-mst-data`, filters, { params });
        } else {
          res = await func.get(`mst-admin/${modelname}${urls.GET_MASTER_DATA_URL}`, { params });
        }

        if (res?.status === 200) {
          const items = res.data?.items || res.data?.data || [];
          setModalData(items);
          setFilteredModalData(items);
          setModalSchema(res.data.modelSkeleton || []);
          setTotalPages(res.data.totalPages || 0);
        }
      } catch (err) {
        toast.error("Failed to load data");
      }
      setLoading(false);
      setBusy(false);
    };
    load();
  }, [reload, currentPage]);

  const triggerSearch = () => { setCurrentPage(1); setReload((r) => !r); };

  const handleSearchInput = (val) => {
    setSearchTerm(val);
    if (!val.trim()) { setCurrentPage(1); setReload((r) => !r); }
  };

  const applyFilters = () => { setCurrentPage(1); setSearchTerm(""); setShowFilter(false); setReload((r) => !r); };

  const resetFilters = () => {
    setFilters({ createdAt: "", updatedAt: "" });
    setSearchTerm("");
    setCurrentPage(1);
    setShowFilter(false);
    setReload((r) => !r);
  };

  const refreshMDM = async () => {
    setShowRefreshButton(false);
    const res = await func.get(urls.REFRESH_MASTER_DATA_URL);
    if (res?.status === 200) {
      toast.success("Data refreshed successfully");
      setReload((r) => !r);
      setReloadContext((c) => !c);
    } else {
      toast.warn("Please try again");
      setShowRefreshButton(true);
    }
  };

  const handleDelete = (item) => { setDeleteUserData(item); setShowDeletePopup(true); };

  const handleAccept = async () => {
    setShowDeleteButton(false);
    if (filteredData?.length === 1) setCurrentPage((p) => Math.max(1, p - 1));
    const res = await func.del(`mst-admin/${modelname}${urls.DELETE_MASTER_DATA_URL}${deleteUserData._id}`);
    setShowDeleteButton(true);
    if (res?.status === 200) {
      toast.success("Entry deleted successfully");
      setDeleteUserData({});
      setShowDeletePopup(false);
      setShowRefreshButton(true);
      setReload((r) => !r);
      setReloadContext(!reloadContext);
      return;
    }
    toast.warn("Delete failed, please try again");
  };

  const renderValue = (item, heading) => {
    const value = getNestedValue(item, heading);
    if (heading === "createdAt" || heading === "updatedAt")
      return value ? new Date(value).toLocaleString("en-IN") : "—";
    if (typeof value === "boolean")
      return <FontAwesomeIcon icon={value ? faCheckCircle : faTimesCircle} className={value ? "text-green-600" : "text-red-500"} />;
    if (Array.isArray(value)) return value.join(", ") || "—";
    if (typeof value === "object" && value !== null) return value.name || JSON.stringify(value);
    if (typeof value === "number") return value >= 0 ? value : "—";
    return value || "—";
  };

  const visibleSchema = modalSchema.filter((f) => f !== "_id" && f !== "__v" && f !== "description");
  const colCount = visibleSchema.length + 2; // sl + schema + actions

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="text-white/70 hover:text-white transition"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <FontAwesomeIcon icon={faDatabase} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">{displayName}</h1>
              <p className="text-white/70 text-sm">Master data records</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showRefreshButton && (
              <button
                onClick={refreshMDM}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
              >
                <FontAwesomeIcon icon={faRefresh} />
                Refresh
              </button>
            )}
            <button
              onClick={() => setShowFilter(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faSlidersH} />
              Filters
            </button>
            <button
              onClick={() => navigate(`/master-data-management/add/${modelname}`, {
                state: { returnTo: `/master-data-management/modellist/${modelname}` },
              })}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Data
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && triggerSearch()}
              maxLength={60}
              placeholder="Search by any field..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <button
            onClick={triggerSearch}
            disabled={busy}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow disabled:opacity-50"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">#</th>
                  {visibleSchema.map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {normalizeHeading(h)}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(colCount)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faDatabase} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No records found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, i) => (
                    <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-400 text-xs">{(currentPage - 1) * limit + i + 1}</td>
                      {visibleSchema.map((heading) => (
                        <td key={heading} className="px-4 py-3 text-gray-600 max-w-[200px]">
                          <span className="block truncate">{renderValue(item, heading)}</span>
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        {!item.isDeleted ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/master-data-management/edit/${modelname}/${item._id}`, {
                                state: { returnTo: `/master-data-management/modellist/${modelname}` },
                              })}
                              className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                              Edit
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No actions</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-5">
            <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
            <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
            {currentPage > 3 && <span className="px-2 text-white/60">…</span>}
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                  p === currentPage ? "bg-white text-indigo-700" : "text-white/70 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            {currentPage < totalPages - 2 && <span className="px-2 text-white/60">…</span>}
            <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
            <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
          </div>
        )}

        {!loading && (
          <p className="text-white/50 text-xs mt-3 text-right">
            {filteredData.length} of {modalData.length} records shown
          </p>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      <ConfirmationPopup
        show={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        onCancel={() => setShowDeletePopup(false)}
        onAccept={handleAccept}
        showDeleteButton={showDeleteButton}
        question={<span>Are you sure you want to delete this entry?</span>}
        title="Delete Confirmation"
        acceptText="Yes, Delete"
        cancelText="Cancel"
      />

      {/* Filter Modal */}
      {showFilter && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowFilter(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">Advanced Filter</h3>
              <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto grid grid-cols-2 gap-4">
              {modalSchema.filter((f) => f !== "createdAt" && f !== "updatedAt").map((field) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{normalizeHeading(field)}</label>
                  {field === "isDeleted" ? (
                    <select
                      value={filters[field] || ""}
                      onChange={(e) => setFilters({ ...filters, [field]: e.target.value })}
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
                    >
                      <option value="">Any</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={filters[field] || ""}
                      onChange={(e) => setFilters({ ...filters, [field]: e.target.value })}
                      placeholder="Enter value"
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                    />
                  )}
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Created At</label>
                <input
                  type="date"
                  value={filters.createdAt}
                  onChange={(e) => setFilters({ ...filters, createdAt: e.target.value })}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Updated At</label>
                <input
                  type="date"
                  value={filters.updatedAt}
                  onChange={(e) => setFilters({ ...filters, updatedAt: e.target.value })}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
              <button onClick={resetFilters} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                Reset
              </button>
              <button onClick={applyFilters} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:bg-white/10"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default ModelListing;
